import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    FormSelectChoice,
    StudentPersonalInfoResponse,
    Locale,
    VerificationStepsEnum,
    hasFailedInstantMatch,
    isFormFilled,
    setRef,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableLocaleChoices,
    ChangeLocaleComponent,
    LogoComponent,
    MarketConsentWrapper,
    PhoneNumberComponent as PhoneNumber,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    FormFooterComponent as FormFooter,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    ErrorId,
    setMetadata,
} from '@sheerid/jslib';

import { BirthDateComponent as BirthDate } from '../FormFields/BirthDateComponent';
import { EmailComponent as Email } from '../FormFields/EmailComponent';
import { getQueryParamsByName, sendPostMessage } from '../../lib/utils/browser';
import { mapSoftwareTypeToHumanReadable, getMajorChoices, getClickTrackingMessage } from '../../lib/stepComponentHelpers';
import { SoftwareType } from '../../lib/types/types';
import { ConfirmEmailComponent as ConfirmEmail } from '../FormFields/ConfirmEmailComponent';
import { MajorComponent } from '../FormFields/MajorComponent';
import { StepErrorComponent } from './StepErrorComponent';

interface StepStudentPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    // eslint-disable-next-line prefer-destructuring
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const softwareType = getQueryParamsByName('type') as SoftwareType;
    const countryParam = getQueryParamsByName('country');
    const softwareTypeMessage = mapSoftwareTypeToHumanReadable(softwareType, intl);
    const clickTrackingMessage = getClickTrackingMessage('collectStudentPersonalInfo', softwareType);
    const validParams = (softwareTypeMessage: string, softwareType: string, countryParam: string) => {
        if (!softwareTypeMessage) return false;
        if (!countryParam) return false;
        if (countryParam !== 'US' && countryParam !== 'CA') return false;
        if (countryParam === 'US' && softwareType !== 'tax') return false;

        return true;
    };

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };


    if (!validParams(softwareTypeMessage, softwareType, countryParam)) {
        return <StepErrorComponent verificationService={verificationService} errorId="invalidSoftwareType" />;
    }

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            { failedInstantMatch
                ? (
                    <div className="sid-header">
                        <div className="sid-l-horz-center">
                            <LogoComponent verificationService={verificationService} />
                        </div>

                        <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and birthdate must match what is on your official school records."
                            />
                        </div>
                    </div>
                )
                : (
                    <div className="sid-header">
                        <div className="sid-l-horz-center">
                            <LogoComponent verificationService={verificationService} />
                        </div>

                        <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="step.personalInfo.title"
                                defaultMessage="Student Registration"
                            />
                        </div>

                        <div className="sid-header__subtitle">
                            {softwareType === 'tax'
                                ? (
                                    <>
                                        {countryParam === 'CA'
                                            ? (
                                                <FormattedHTMLMessage
                                                    id="step.personalInfo.subtitle"
                                                    defaultMessage="Students at accredited academic institutions are eligible for a one-year student registration. College, University, and Trade-School students please complete the form below to confirm eligibility and unlock your free license for {software}. High School students, please have your teacher complete the educator registration."
                                                    values={{
                                                        software: softwareTypeMessage,
                                                    }}
                                                />
                                            )
                                            : (
                                                <FormattedHTMLMessage
                                                    id="step.personalInfo.subtitleUS"
                                                    defaultMessage="Students at accredited academic institutions are eligible for a one-year student registration. College, University, and Trade-School students please complete the form below to confirm eligibility and unlock your free license for ProConnect. High School students, please have your teacher complete the educator registration."
                                                />
                                            )}
                                    </>
                                )
                                : (
                                    <FormattedHTMLMessage
                                        id="step.personalInfo.subtitle"
                                        defaultMessage="Students at accredited academic institutions are eligible for a one-year student registration. College, University, and Trade-School students please complete the form below to confirm eligibility and unlock your free license for {software}. High School students, please have your teacher complete the educator registration."
                                        values={{
                                            software: softwareTypeMessage,
                                        }}
                                    />
                                )}
                        &nbsp;
                            <HowDoesVerifyingWork verificationService={verificationService} />
                        </div>
                    </div>
                )}

            {
                localeChoices.length > 1 && (countryParam === 'CA')
                    ? (
                        <ChangeLocaleComponent
                            options={localeChoices}
                            value={viewModel.localeChoice || defaultLocaleChoice}
                            isErrored={false}
                            onChange={(localeChoice: FormSelectChoice<Locale, string>) => {
                                updateLocale(localeChoice);
                                setMetadata({ locale: localeChoice.value });
                            }}
                        />
                    )
                    : null
            }

            <div className="sid-required-fields sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="requiredFields"
                    defaultMessage="All fields are required"
                />
            </div>

            <div className="sid-l-space-top-md" />

            <FirstName
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastName
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                )}
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <ConfirmEmail
                value={viewModel?.metadata['confirm-email'] ? viewModel.metadata['confirm-email'] : ''}
                isErrored={!!fieldValidationErrors['confirm-email']}
                errorId={fieldValidationErrors['confirm-email'] as ErrorId}
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, 'confirm-email': newValue });
                        updateFieldValidationErrorsByFieldId('confirm-email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                verificationService={verificationService}
                onChange={(newValue: string) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <MajorComponent
                options={getMajorChoices(intl)}
                value={
                    viewModel?.metadata['course-type']
                        ? {
                            value: viewModel.metadata['course-type'],
                            label: intl.formatMessage({ id: `${(viewModel.metadata['course-type'] as string).toLowerCase()}`, defaultMessage: `${viewModel.metadata['course-type']}` }),
                        }
                        : ''
                }
                isErrored={!!fieldValidationErrors['course-type']}
                onChange={(majorChoice: FormSelectChoice<Locale, string>) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, 'course-type': majorChoice.value });
                    updateFieldValidationErrorsByFieldId('course-type', majorChoice.value, verificationService);
                }}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumber
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            onChange={
                                (newValue: string) => {
                                    updateStudentViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    )
                    : null
            }

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-lg">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => {
                            sendPostMessage(clickTrackingMessage, 'https://www.intuit.com');
                            submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo);
                        }}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />}
                        </span>
                    </button>
                </div>
            </div>

            <div className="sid-l-space-top-lg" />

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
